import { AxiosResponse, AxiosError } from "axios";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Input, Button } from "reactstrap";
import { getMessageThreadBankPortal } from "../../data/MessageCenter";
import {
  IReloadMessageComponentModel,
  MessageThreadModel,
} from "../../models/MessageCenterModels";
import Service from "../../services";
import { APP_Config } from "../../utils/AppConfig";
import { catchError } from "../../utils/Utils";
import MessageThreadCard from "./MessageThreadCard";
import { toast } from "react-toastify";
import { ReactComponent as EditIcon } from "../../images/MessageCenter/edit.svg";
import MessageFormModal from "./MessageFormModal";
import { useNavigate, useParams } from "react-router";
import { RoutesEnum } from "../../utils/RoutesUtil";
import FinSpinner from "../../commonfields/FinSpinner";
import { catchErrorGeneric } from "../../utils/GeneralUtils";

interface IMessageThreadsListProps {
  config: any;
  conversation: MessageThreadModel | undefined;
  setConversation: React.Dispatch<
    React.SetStateAction<MessageThreadModel | undefined>
  >;
  reload?: IReloadMessageComponentModel;
  setReload?: React.Dispatch<
    React.SetStateAction<IReloadMessageComponentModel>
  >;
}

const MessageThreadsList = (props: IMessageThreadsListProps) => {
  const { config, conversation, setConversation, reload, setReload } = props;
  const service = useMemo(() => new Service(), []);
  const params = useParams();
  const navigate = useNavigate();

  // states //
  const [loading, setLoading] = useState(false);
  const [messageThreads, setMessageThreads] = useState<
    MessageThreadModel[] | []
  >([]);
  const [messageTypeSelect, setMessageTypeSelect] = useState<string>("all");
  const [messageSearch, setMessageSearch] = useState("");
  const [messagesModal, setMessagesModal] = useState(false);
  const [unreadMessages, setUreadMessages] = useState(0);

  // consts //

  // setting api reload time from env js and if value not present set it to 0
  const appReloadTime =
    APP_Config().REACT_APP_RELOAD_TIME !== undefined
      ? parseInt(APP_Config().REACT_APP_RELOAD_TIME) * 60 * 1000
      : 0;

  // handle functions //

  const toggleMessagesModal = () => setMessagesModal((prev) => !prev);

  // set message search value based on the input value change
  const handleConversationSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setMessageSearch(event?.target?.value);
  };

  // set message type ( all conv, archived ) based on select changes
  const handleMessageTypeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setMessageTypeSelect(event?.target?.value);
    setConversation(undefined); // reset the conversation (form) as the type has changed
  };

  // trigger different functionalities based on the type and thread click
  const handleTreadClick = (thread: MessageThreadModel) => {
    if (params?.threadId) {
      navigate(`/${RoutesEnum.MESSAGE_CENTER}`);
    }

    setConversation && setConversation(thread);
    thread && updateThreadReadFlag(thread?.id);

    // open modal
    if (config?.clickModal) {
      setMessagesModal(true);
    }
  };

  // display active conversation
  const isActive = (thread: MessageThreadModel) => {
    if (
      conversation &&
      Object.keys(conversation)?.length > 0 &&
      (conversation as MessageThreadModel)?.id === thread.id
    ) {
      return true;
    } else {
      return false;
    }
  };

  // open modal and set conversation for quick message reply
  const quickMessageReply = (thread: MessageThreadModel) => {
    setConversation && setConversation(thread);
    toggleMessagesModal();
  };

  // reset for new Message creation
  const resetConversation = () => {
    if (params?.threadId) {
      navigate(`/${RoutesEnum.MESSAGE_CENTER}`);
    }
    setConversation && setConversation(undefined);
  };

  // check the message thread with the value provided
  const searchAllFields = (thread: MessageThreadModel, value: string) => {
    let flag = false;
    if (thread?.userDisplayFirstName?.toLowerCase()?.includes(value))
      flag = true;
    else if (thread?.userDisplayLastName?.toLowerCase()?.includes(value))
      flag = true;
    else if (thread?.clientCompany?.toLowerCase()?.includes(value)) flag = true;
    else if (thread?.subject?.toLowerCase()?.includes(value)) flag = true;
    else if (thread?.latestMessage?.toLowerCase()?.includes(value)) flag = true;
    return flag;
  };

  const setFilteredMessageThreads = (data: MessageThreadModel[]) => {
    const searchedResults = messageSearch
      ? data?.filter((each) =>
          searchAllFields(each, messageSearch?.toLowerCase())
        )
      : data;
    const sortedResults = searchedResults
      ?.sort((a: MessageThreadModel, b: MessageThreadModel) => {
        return (
          Date.parse(b.latestMessageTime || "") -
          Date.parse(a.latestMessageTime || "")
        );
      })
      ?.sort((x, y) => {
        return x?.pinnedFlag === y?.pinnedFlag ? 0 : x?.pinnedFlag ? -1 : 1;
      });
    return sortedResults;
  };

  // api calls

  // get all message threads
  const getMessageThreadsByClientPortal = (initLoad: boolean) => {
    initLoad && setLoading(true);
    if (params?.clientId) {
      service
        .getMessageThreadsByClientPortal(params?.clientId)
        .then((res: AxiosResponse<MessageThreadModel[]>) => {
          const sortedResults = setFilteredMessageThreads(res?.data);
          setMessageThreads(sortedResults);
          initLoad && setLoading(false);
        })
        .catch((error: AxiosError) => {
          catchError(error, true);
          initLoad && setLoading(false);
        });
    }
  };

  const getMessageThread = (initLoad: boolean) => {
    initLoad && setLoading(true);
    service
      .getMessageThread()
      .then((res: AxiosResponse<MessageThreadModel[]>) => {
        const filteredResults = res?.data?.filter((each) =>
          messageTypeSelect === "all" ? !each?.archiveFlag : each?.archiveFlag
        );
        const sortedResults = setFilteredMessageThreads(filteredResults);
        setMessageThreads(sortedResults);
        initLoad && setLoading(false);
      })
      .catch((error: AxiosError) => {
        catchError(error, true);
        initLoad && setLoading(false);
      });
  };

  const getMessageThreads = useCallback(
    (initLoad = false) => {
      if (config?.apiType === "client") {
            getMessageThread(initLoad);
        //  getMessageThreadsByClientPortal(initLoad);
      } else {
        getMessageThread(initLoad);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageSearch, messageTypeSelect, service]
  );

  const updateThreadReadFlag = (id: string) => {
    service
      .updateThreadReadFlag(id)
      .then((res) => {
        if (res?.status === 200) {
          getMessageThreads();
          config?.messageCount && getUnreadMessageCount();
        } else {
          // toast.error("Something went Wrong");
          catchErrorGeneric("Something went wrong");
        }
      })
      .catch((error: AxiosError) => {
        catchError(error, true);
      });
  };

  const getUnreadMessageCount = () => {
    service
      .getUnreadMessageCount()
      .then((res) => setUreadMessages(res?.data))
      .catch((err) => catchError(err, true));
  };

  // useeffects
  useEffect(() => {
    getMessageThreads(true);
    config?.messageCount && getUnreadMessageCount();
    const interval = setInterval(() => {
      getMessageThreads();
      config?.messageCount && getUnreadMessageCount();
    }, appReloadTime);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMessageThreads]);

  useEffect(() => {
    if (reload?.list) {
      getMessageThreads();
      setReload &&
        setReload((prev) => ({
          ...prev,
          list: false,
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    if (params?.threadId && messageThreads) {
      const selectedThread = messageThreads?.find(
        (each) => each?.id === params?.threadId
      );
      if (selectedThread) {
        setConversation && setConversation(selectedThread);
        selectedThread?.archiveFlag && setMessageTypeSelect("archived");
      } else {
        setConversation && setConversation(undefined);
      }
    }
  }, [messageThreads, params?.threadId, setConversation]);

  return (
    <div className="message-threads-list-main">
      {loading ? <FinSpinner /> : <></>}

      {config?.messageCount && unreadMessages > 0 ? (
        <div className="message-count">
          {unreadMessages}
          {config?.messagesText ? (
            <span className="messages-text"> messages</span>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      {config?.createNew ? (
        <div className="newMessageIcon" onClick={resetConversation}>
          <EditIcon className="newMessageIcon-image" />
        </div>
      ) : (
        <></>
      )}

      {config?.messageTypeSelect ? (
        <select
          name="messageTypeSelect"
          id="messageTypeSelect"
          className="messageTypeSelect"
          value={messageTypeSelect}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            handleMessageTypeSelect(e)
          }
        >
          <option value="all">All Conversations</option>
          <option value="archived">Archived</option>
        </select>
      ) : (
        <></>
      )}

      {config?.threadSearch ? (
        <div className="search">
          <Input
            type="text"
            className="Message Search"
            placeholder={config?.searchPlaceholder || "Search"}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleConversationSearch(e)
            }
          />
        </div>
      ) : (
        <></>
      )}

      <div className="conversationsBox">
        {messageThreads?.length > 0 ? (
          messageThreads?.map((each) => (
            <MessageThreadCard
              key={each?.id}
              messageThread={each}
              isActive={isActive(each)}
              handleTreadClick={handleTreadClick}
              config={config}
              quickMessageReply={quickMessageReply}
            />
          ))
        ) : (
          <p className="noMessages">
            {messageTypeSelect === "all"
              ? "No Messages available"
              : "No archived messages to display"}
          </p>
        )}
      </div>

      {config?.createNew ? (
        <Button
          color="outline-primary"
          className="createMessage"
          onClick={resetConversation}
        >
          New Message
        </Button>
      ) : (
        <></>
      )}

      {conversation ? (
        <MessageFormModal
          config={config}
          messagesModal={messagesModal}
          toogleMessagesModal={toggleMessagesModal}
          conversation={conversation}
          setConversation={setConversation}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default MessageThreadsList;
